
export const browserWindow = (): Partial<Window & typeof globalThis> => window;

type OptionalNavigator = Omit<Partial<Navigator>, 'storage'> & {
  storage?: Partial<StorageManager>;
  deviceMemory?: number;
  userAgentData?: { platform?: string };
};

export const browserNavigator = (): OptionalNavigator => navigator;

export const browserCrypto = (): Partial<Crypto> => globalThis.crypto;

export const createGlProgram = (gl: WebGLRenderingContext): WebGLProgram | null => gl.createProgram();

const mediaQuery = (query: string): Partial<MediaQueryList> | undefined =>
  browserWindow().matchMedia?.(query);

export const matchesMediaQuery = (query: string): boolean =>
  typeof window !== 'undefined' && (mediaQuery(query)?.matches ?? false);

export const watchMediaQuery = (query: string, onChange: (matches: boolean) => void): (() => void) => {
  const mq = mediaQuery(query);
  const report = () => onChange(mq?.matches ?? false);
  report();
  mq?.addEventListener?.('change', report);
  return () => mq?.removeEventListener?.('change', report);
};

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export const runWhenIdle = (
  run: () => void,
  { timeoutMs, fallbackDelayMs = 0 }: { timeoutMs?: number; fallbackDelayMs?: number } = {},
): (() => void) => {
  const w = browserWindow();
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(run, timeoutMs === undefined ? undefined : { timeout: timeoutMs });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(run, fallbackDelayMs);
  return () => {
    window.clearTimeout(id);
  };
};

export const shareOrCopyLink = async (text: string, url: string): Promise<void> => {
  const nav = browserNavigator();
  if (nav.share) await nav.share({ text, url });
  else await navigator.clipboard.writeText(url);
};
